import type { RollbackFromStatus, RollbackKind, RollbackReasonCode } from '@discovr/types';
import { campaignCopy } from '../copy/campaignCopy';

const REASONS = campaignCopy.creator.rollback.reasons;
const NEUTRAL: Readonly<Record<string, string>> = campaignCopy.creator.rollback.neutralReasonLabels;

export function rollbackReasonsFor(kind: RollbackKind): ReadonlyArray<{ code: RollbackReasonCode; label: string }> {
  return REASONS[kind];
}

export function isRollbackReasonFor(kind: RollbackKind, value: string): value is RollbackReasonCode {
  return REASONS[kind].some((r) => r.code === value);
}

export function rollbackKindForFromStatus(fromStatus: RollbackFromStatus): RollbackKind {
  return fromStatus === 'content_pending' ? 'script' : 'content';
}

const findLabel = (kind: RollbackKind, code: string) => REASONS[kind].find((r) => r.code === code)?.label;

/** Label for a stored reason code; a code is labelled per kind (needs_changes differs for script/content). */
export function rollbackReasonLabel(kind: RollbackKind | null, code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return '';
  const own = kind ? findLabel(kind, trimmed) : undefined;
  if (own) return own;
  const neutral = Object.hasOwn(NEUTRAL, trimmed) ? NEUTRAL[trimmed] : undefined;
  if (neutral) return neutral;
  const kinds = Object.keys(REASONS) as RollbackKind[];
  const any = kinds.map((k) => findLabel(k, trimmed)).find((label) => label !== undefined);
  return any ?? trimmed.replace(/_/g, ' ');
}

const LEGACY_REASON_MAX = 1000;

/** Free-text `reason` for a backend that predates reason_code; a newer one ignores it when reason_code is set. */
export function legacyRollbackReason(fromStatus: RollbackFromStatus, code: RollbackReasonCode, note = ''): string {
  const label = rollbackReasonLabel(rollbackKindForFromStatus(fromStatus), code);
  const text = note ? `${label}: ${note}` : label;
  return Array.from(text).slice(0, LEGACY_REASON_MAX).join('');
}
